const KEYCODE = {
  DOWN: 40,
  LEFT: 37,
  RIGHT: 39,
  UP: 38,
  HOME: 36,
  END: 35,
}

const template = document.createElement('template')
template.innerHTML = `
    <style>
      :host {
        display: flex;
        flex-wrap: wrap;
      }
      ::slotted(bao-tab-panel) {
        flex-basis: 100%;
      }
    </style>
    
    <slot name="tab"></slot>
    <slot name="panel"></slot>
  `

class BaoTabs extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.attachShadow({ mode: 'open' })

    this.shadowRoot.appendChild(template.content.cloneNode(true))
  }

  setupListeners () {
    super.setupListeners()

    this._onSlotChange = this._onSlotChange.bind(this)

    this._tabSlot = this.shadowRoot.querySelector('slot[name=tab]')
    this._panelSlot = this.shadowRoot.querySelector('slot[name=panel]')

    this.listeners.add(this._tabSlot, 'slotchange', this._onSlotChange)
    this.listeners.add(this._panelSlot, 'slotchange', this._onSlotChange)

    this.listeners.add(this, 'keydown', this._onKeyDown)
    this.listeners.add(this, 'click', this._onClick)
  }

  connectedCallback () {
    super.connectedCallback()

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'tablist')
    }

    // eslint-disable-next-line compat/compat
    Promise.all([
      customElements.whenDefined('bao-tab'),
      customElements.whenDefined('bao-tab-panel'),
    ]).then(_ => this._linkPanels())
  }

  _onSlotChange () {
    this._linkPanels()
  }

  _linkPanels () {
    const tabs = this.allTabs

    tabs.forEach(tab => {
      const panel = tab.nextElementSibling
      if (panel.tagName.toLowerCase() !== 'bao-tab-panel') {
        console.error(
          `Tab #${tab.id} is not a` + `sibling of a <bao-tab-panel>`,
        )
        return
      }

      tab.setAttribute('aria-controls', panel.id)
      panel.setAttribute('aria-labelledby', tab.id)
    })

    const selectedTab = tabs.find(tab => tab.selected) || tabs[0]

    this._selectTab(selectedTab)
  }

  _panelForTab (tab) {
    const panelId = tab.getAttribute('aria-controls')

    return this.querySelector(`#${panelId}`)
  }

  reset () {
    this.allTabs.forEach(tab => (tab.selected = false))
    this.allPanels.forEach(panel => (panel.hidden = true))
  }

  _selectTab (newTab) {
    this.reset()

    const newPanel = this._panelForTab(newTab)

    if (!newPanel) {
      throw new Error(`No panel with id ${newPanel.id}`)
    }

    newTab.selected = true
    newPanel.hidden = false
    newTab.focus()
  }

  _onKeyDown (event) {
    if (event.target.getAttribute('role') !== 'tab') return
    if (event.altKey) return

    let newTab

    switch (event.keyCode) {
      case KEYCODE.LEFT:
      case KEYCODE.UP:
        newTab = this.prevTab
        break

      case KEYCODE.RIGHT:
      case KEYCODE.DOWN:
        newTab = this.nextTab
        break

      case KEYCODE.HOME:
        newTab = this.firstTab
        break

      case KEYCODE.END:
        newTab = this.lastTab
        break

      default:
        return
    }

    event.preventDefault()

    this._selectTab(newTab)
  }

  _onClick (event) {
    if (event.target.getAttribute('role') !== 'tab') return

    this._selectTab(event.target)
  }

  get allPanels () {
    return Array.from(this.querySelectorAll('bao-tab-panel'))
  }

  get allTabs () {
    return Array.from(this.querySelectorAll('bao-tab'))
  }

  get prevTab () {
    const newIdx = this.allTabs.findIndex(tab => tab.selected) - 1

    return this.allTabs[(newIdx + this.allTabs.length) % this.allTabs.length]
  }

  get firstTab () {
    return this.allTabs[0]
  }

  get lastTab () {
    return this.allTabs[this.allTabs.length - 1]
  }

  get nextTab () {
    const newIdx = this.allTabs.findIndex(tab => tab.selected) + 1

    return this.allTabs[newIdx % this.allTabs.length]
  }
}

if (!customElements.get('bao-tabs')) {
  customElements.define('bao-tabs', BaoTabs)
}

let tabCounter = 0

class BaoTab extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['selected']
  }

  connectedCallback () {
    super.connectedCallback()

    this.setAttribute('role', 'tab')
    if (!this.id) {
      this.id = `bao-tab-generated-${tabCounter++}`
    }

    this.setAttribute('aria-selected', 'false')
    this.setAttribute('tabindex', -1)
    this._upgradeProperty('selected')
  }

  attributeChangedCallback () {
    const value = this.hasAttribute('selected')
    this.setAttribute('aria-selected', value)
    this.setAttribute('tabindex', value ? 0 : -1)
  }

  get selected () {
    return this.hasAttribute('selected')
  }

  set selected (value) {
    value = Boolean(value)

    if (value) {
      this.setAttribute('selected', '')
    } else {
      this.removeAttribute('selected')
    }
  }
}

if (!customElements.get('bao-tab')) {
  customElements.define('bao-tab', BaoTab)
}

let panelCounter = 0

class BaoTabPanel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.setAttribute('role', 'tabpanel')

    if (!this.id) {
      this.id = `bao-tab-panel-generated-${panelCounter++}`
    }
  }
}

if (!customElements.get('bao-tab-panel')) {
  customElements.define('bao-tab-panel', BaoTabPanel)
}
